/**
 * Componente de controle de áudio otimizado para minimizar o uso de participant-minutes
 * Inclui:
 * - Desconexão automática quando o componente é desmontado
 * - Entrada na sala sob demanda (botão "Entrar/Sair da Sala")
 * - Monitoramento de inatividade
 * - Botão explícito para entrar/sair da sala
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DailyProvider, DailyAudio } from '@daily-co/daily-react';
import DailySingletonService from '../../services/DailySingletonService';
import DailyRoomService from '../../services/DailyRoomService';
import { useLanguage } from '../../i18n/LanguageContext';

interface AudioControlsOptimizedProps {
  roomUrl?: string;
  inactivityTimeout?: number; // Tempo em minutos para considerar o usuário inativo
  isChatOpen?: boolean; // Estado do chat (aberto ou fechado)
}

const AudioControlsOptimized: React.FC<AudioControlsOptimizedProps> = ({
  roomUrl = 'https://multiverso.daily.co/audio-global',
  inactivityTimeout = 15, // 15 minutos de inatividade por padrão
  isChatOpen = false // Chat fechado por padrão
}) => {
  const [callObject, setCallObject] = useState<any>(null);
  const [isMicrophoneEnabled, setIsMicrophoneEnabled] = useState<boolean>(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState<boolean>(true);
  const [meetingState, setMeetingState] = useState<string>('idle');
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isInRoom, setIsInRoom] = useState<boolean>(false);
  const [participantCount, setParticipantCount] = useState<number>(0);
  const [isLeaving, setIsLeaving] = useState<boolean>(false); // Estado para controlar o processo de saída

  // Referências para monitoramento de inatividade e tempo de sessão
  const lastActivityRef = useRef<number>(Date.now());
  const sessionStartTimeRef = useRef<number | null>(null); // Tempo de início da sessão
  const inactivityTimerRef = useRef<number | null>(null);
  const sessionTimerRef = useRef<number | null>(null); // Timer para limitar o tempo de sessão
  const removeEventListenersRef = useRef<(() => void)[]>([]);
  const participantCountTimerRef = useRef<number | null>(null); // Timer para atualizar a contagem de participantes
  const prevParticipantCountRef = useRef<number>(0); // Referência para armazenar a contagem anterior de participantes

  // Tempo máximo de sessão em minutos (2 horas)
  const MAX_SESSION_TIME = 120;

  // Atualizar contagem de participantes quando estiver na sala
  const updateParticipantCount = useCallback((callObj: any) => {
    if (!callObj) return;

    try {
      const participants = callObj.participants();
      if (participants) {
        const count = Object.keys(participants).length;
        setParticipantCount(count);
        console.log(`[AudioControlsOptimized] Contagem de participantes atualizada via objeto de chamada: ${count}`);
      }
    } catch (error) {
      console.error('[AudioControlsOptimized] Erro ao obter participantes:', error);
    }
  }, []);

  // Atualizar contagem de participantes via API
  const updateParticipantCountViaAPI = useCallback(async () => {
    try {
      // Extrair o nome da sala da URL
      const roomName = roomUrl.split('/').pop() || '';

      // Obter a contagem de participantes via API
      const count = await DailyRoomService.getRoomParticipantCount(roomName);

      // Sempre atualizar a contagem de participantes
      setParticipantCount(count);
      // Reduzir logs repetitivos - apenas logar em desenvolvimento ou quando houver mudança
      if (process.env.NODE_ENV === 'development' || prevParticipantCountRef.current !== count) {
        console.log(`[AudioControlsOptimized] Contagem de participantes atualizada via API: ${count}`);
        // Atualizar a referência para a próxima comparação
        prevParticipantCountRef.current = count;
      }

      // Se não estiver na sala e a contagem for maior que 0, verificar novamente
      // para garantir que a contagem esteja correta
      if (!isInRoom && count > 0) {
        setTimeout(() => {
          DailyRoomService.getRoomParticipantCount(roomName)
            .then(newCount => {
              console.log(`[AudioControlsOptimized] Verificação adicional: ${newCount} participantes`);
              setParticipantCount(newCount);
            })
            .catch(err => {
              console.error('[AudioControlsOptimized] Erro na verificação adicional:', err);
            });
        }, 500);
      }
    } catch (error) {
      console.error('[AudioControlsOptimized] Erro ao obter participantes via API:', error);
    }
  }, [roomUrl, isInRoom]);

  // Iniciar o timer para atualizar a contagem de participantes periodicamente
  useEffect(() => {
    // Atualizar a contagem de participantes imediatamente
    updateParticipantCountViaAPI();

    // Configurar timer para atualizar a contagem de participantes a cada 1 segundo
    participantCountTimerRef.current = window.setInterval(() => {
      updateParticipantCountViaAPI();
    }, 1000); // 1 segundo

    // Limpar o timer quando o componente for desmontado
    return () => {
      if (participantCountTimerRef.current !== null) {
        window.clearInterval(participantCountTimerRef.current);
        participantCountTimerRef.current = null;
      }
    };
  }, [updateParticipantCountViaAPI]);

  // Inicializar o Daily.co (apenas inicializar, não entrar na sala automaticamente)
  useEffect(() => {
    let isMounted = true;

    const initializeDaily = async () => {
      try {
        console.log('[AudioControlsOptimized] Inicializando Daily.co...');

        // Obter a instância do serviço Singleton
        const dailyService = DailySingletonService.getInstance();

        // Inicializar o objeto de chamada
        const dailyCallObject = await dailyService.initialize({
          url: roomUrl,
          audioSource: true,
          videoSource: false,
          subscribeToTracksAutomatically: true
        });

        // Configurar eventos
        const meetingStateListener = dailyService.addEventListener('meeting-state-updated', (event: any) => {
          const state = event?.state || 'unknown';
          console.log(`[AudioControlsOptimized] Estado da reunião atualizado: ${state}`);
          if (isMounted) {
            setMeetingState(state);

            // Atualizar isInRoom com base no estado da reunião
            if (state === 'joined-meeting') {
              setIsInRoom(true);
              setIsLeaving(false);
            } else if (state === 'left-meeting' || state === 'error') {
              setIsInRoom(false);
              setIsLeaving(false);
            }
          }
        });
        removeEventListenersRef.current.push(meetingStateListener);

        // Configurar evento para atualização de participante
        const participantUpdatedListener = dailyService.addEventListener('participant-updated', (event: any) => {
          if (event?.participant?.local && event?.updatedValues?.audio !== undefined) {
            console.log(`[AudioControlsOptimized] Estado do microfone atualizado: ${event.updatedValues.audio}`);
            if (isMounted) {
              setIsMicrophoneEnabled(event.updatedValues.audio);
            }
          }
        });
        removeEventListenersRef.current.push(participantUpdatedListener);

        // Configurar evento para quando um participante entra
        const participantJoinedListener = dailyService.addEventListener('participant-joined', () => {
          if (isMounted) {
            updateParticipantCount(dailyCallObject);
          }
        });
        removeEventListenersRef.current.push(participantJoinedListener);

        // Configurar evento para quando um participante sai
        const participantLeftListener = dailyService.addEventListener('participant-left', () => {
          if (isMounted) {
            updateParticipantCount(dailyCallObject);
          }
        });
        removeEventListenersRef.current.push(participantLeftListener);

        // Configurar evento para quando ocorre um erro
        const errorListener = dailyService.addEventListener('error', (event: any) => {
          console.error(`[AudioControlsOptimized] Erro do Daily.co:`, event);
          if (isMounted) {
            // Se estiver tentando sair da sala, considerar como saído mesmo com erro
            if (isLeaving) {
              setIsInRoom(false);
              setIsLeaving(false);
            }
          }
        });
        removeEventListenersRef.current.push(errorListener);

        // Verificar se o componente ainda está montado
        if (isMounted) {
          console.log('[AudioControlsOptimized] Objeto de chamada inicializado com sucesso');
          setCallObject(dailyCallObject);
          setIsInitialized(true);

          // Não entrar na sala automaticamente - esperar pelo botão
        }
      } catch (error) {
        console.error('[AudioControlsOptimized] Erro ao inicializar Daily.co:', error);
      }
    };

    // Inicializar o Daily.co
    initializeDaily();

    // Configurar monitoramento de atividade do usuário
    setupActivityMonitoring();

    // Limpar ao desmontar
    return () => {
      isMounted = false;

      // Remover ouvintes de eventos
      removeEventListenersRef.current.forEach(removeListener => removeListener());
      removeEventListenersRef.current = [];

      // Limpar timers
      if (inactivityTimerRef.current !== null) {
        window.clearInterval(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }

      // Limpar timer de sessão máxima
      if (sessionTimerRef.current !== null) {
        window.clearInterval(sessionTimerRef.current);
        sessionTimerRef.current = null;
      }

      // Limpar timer de contagem de participantes
      if (participantCountTimerRef.current !== null) {
        window.clearInterval(participantCountTimerRef.current);
        participantCountTimerRef.current = null;
      }

      // Remover event listeners de atividade
      cleanupActivityMonitoring();

      // Sair da sala se estiver nela
      if (callObject && (isInRoom || meetingState === 'joined-meeting')) {
        console.log('[AudioControlsOptimized] Saindo da sala ao desmontar componente...');
        callObject.leave().catch(error => {
          console.error('[AudioControlsOptimized] Erro ao sair da sala:', error);
        });
      }
    };
  }, [roomUrl, isLeaving]);



  // Configurar monitoramento de atividade do usuário
  const setupActivityMonitoring = useCallback(() => {
    // Atualizar timestamp da última atividade
    const updateLastActivity = () => {
      lastActivityRef.current = Date.now();
    };

    // Adicionar event listeners para detectar atividade do usuário
    window.addEventListener('mousemove', updateLastActivity);
    window.addEventListener('mousedown', updateLastActivity);
    window.addEventListener('keypress', updateLastActivity);
    window.addEventListener('touchstart', updateLastActivity);
    window.addEventListener('scroll', updateLastActivity);

    // Iniciar timer para verificar inatividade
    inactivityTimerRef.current = window.setInterval(() => {
      const now = Date.now();
      const inactiveTime = (now - lastActivityRef.current) / (1000 * 60); // Converter para minutos

      if (inactiveTime >= inactivityTimeout && callObject && isInRoom) {
        console.log(`[AudioControlsOptimized] Usuário inativo por ${inactiveTime.toFixed(2)} minutos. Saindo da sala...`);
        leaveRoom();
      }
    }, 60000); // Verificar a cada minuto

    return () => {
      cleanupActivityMonitoring();
    };
  }, [inactivityTimeout]);

  // Limpar monitoramento de atividade
  const cleanupActivityMonitoring = useCallback(() => {
    window.removeEventListener('mousemove', () => {});
    window.removeEventListener('mousedown', () => {});
    window.removeEventListener('keypress', () => {});
    window.removeEventListener('touchstart', () => {});
    window.removeEventListener('scroll', () => {});

    if (inactivityTimerRef.current !== null) {
      window.clearInterval(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  // Alternar microfone
  const toggleMicrophone = useCallback(async () => {
    if (callObject) {
      console.log(`[AudioControlsOptimized] ${isMicrophoneEnabled ? 'Desativando' : 'Ativando'} microfone...`);
      try {
        await callObject.setLocalAudio(!isMicrophoneEnabled);
        setIsMicrophoneEnabled(!isMicrophoneEnabled);
        console.log(`[AudioControlsOptimized] Microfone ${!isMicrophoneEnabled ? 'ativado' : 'desativado'} com sucesso`);

        // Atualizar timestamp da última atividade
        lastActivityRef.current = Date.now();
      } catch (error) {
        console.error('[AudioControlsOptimized] Erro ao alternar microfone:', error);
      }
    }
  }, [callObject, isMicrophoneEnabled]);

  // Alternar áudio
  const toggleAudio = useCallback(async () => {
    if (callObject) {
      console.log(`[AudioControlsOptimized] ${isAudioEnabled ? 'Desativando' : 'Ativando'} áudio...`);
      try {
        // Silenciar todos os participantes remotos
        const participants = callObject.participants();
        if (participants) {
          // Não podemos realmente silenciar o áudio dos outros participantes,
          // mas podemos controlar se queremos ouvir o áudio deles ou não
          // Isso é feito ajustando o volume do elemento de áudio
          const audioElements = document.querySelectorAll('audio');
          audioElements.forEach((audio: HTMLAudioElement) => {
            // Importante: isAudioEnabled = true significa que queremos ouvir o áudio
            // então audio.muted deve ser !isAudioEnabled
            audio.muted = isAudioEnabled; // Invertido para funcionar corretamente
          });
        }

        // Inverter o estado do áudio
        setIsAudioEnabled(!isAudioEnabled);
        console.log(`[AudioControlsOptimized] Áudio ${!isAudioEnabled ? 'ativado' : 'desativado'} com sucesso`);

        // Atualizar timestamp da última atividade
        lastActivityRef.current = Date.now();
      } catch (error) {
        console.error('[AudioControlsOptimized] Erro ao alternar áudio:', error);
      }
    }
  }, [callObject, isAudioEnabled]);

  // Entrar na sala
  const joinRoom = useCallback(async () => {
    if (callObject && !isInRoom && !isLeaving) {
      console.log('[AudioControlsOptimized] Entrando na sala...');
      try {
        await callObject.join();
        console.log('[AudioControlsOptimized] Entrou na sala com sucesso');
        setIsInRoom(true);

        // Solicitar permissão para notificações se o navegador suportar
        if ('Notification' in window && Notification.permission === 'default') {
          Notification.requestPermission().then(permission => {
            console.log(`[AudioControlsOptimized] Permissão de notificação: ${permission}`);
          }).catch(error => {
            console.error('[AudioControlsOptimized] Erro ao solicitar permissão de notificação:', error);
          });
        }

        // Verificar o estado atual do microfone
        const participants = callObject.participants();
        if (participants && participants.local) {
          const isAudioOn = participants.local.audio;
          console.log(`[AudioControlsOptimized] Estado inicial do microfone: ${isAudioOn}`);
          setIsMicrophoneEnabled(isAudioOn);
        }

        // Atualizar contagem de participantes
        updateParticipantCount(callObject);

        // Atualizar a contagem de participantes via API também
        updateParticipantCountViaAPI();

        // Atualizar timestamp da última atividade
        const now = Date.now();
        lastActivityRef.current = now;

        // Iniciar o temporizador de sessão máxima
        sessionStartTimeRef.current = now;

        // Configurar timer para verificar o tempo máximo de sessão
        if (sessionTimerRef.current !== null) {
          window.clearInterval(sessionTimerRef.current);
        }

        sessionTimerRef.current = window.setInterval(() => {
          if (sessionStartTimeRef.current && isInRoom) {
            const sessionDuration = (Date.now() - sessionStartTimeRef.current) / (1000 * 60); // Converter para minutos

            // Avisar o usuário quando estiver próximo do limite (15 minutos antes)
            if (sessionDuration >= MAX_SESSION_TIME - 15 && sessionDuration < MAX_SESSION_TIME - 14) {
              console.log(`[AudioControlsOptimized] Aviso: Sessão será encerrada em aproximadamente 15 minutos.`);

              // Mostrar notificação para o usuário se o navegador suportar
              if ('Notification' in window && Notification.permission === 'granted') {
                new Notification(t('sessionTimeWarningTitle'), {
                  body: t('sessionTimeWarning15'),
                  icon: '/favicon.ico'
                });
              }
            }

            // Avisar o usuário quando estiver próximo do limite (5 minutos antes)
            if (sessionDuration >= MAX_SESSION_TIME - 5 && sessionDuration < MAX_SESSION_TIME - 4) {
              console.log(`[AudioControlsOptimized] Aviso: Sessão será encerrada em aproximadamente 5 minutos.`);

              // Mostrar notificação para o usuário se o navegador suportar
              if ('Notification' in window && Notification.permission === 'granted') {
                new Notification(t('sessionTimeWarningTitle'), {
                  body: t('sessionTimeWarning5'),
                  icon: '/favicon.ico'
                });
              }
            }

            // Encerrar a sessão quando atingir o tempo máximo
            if (sessionDuration >= MAX_SESSION_TIME) {
              console.log(`[AudioControlsOptimized] Tempo máximo de sessão (${MAX_SESSION_TIME} minutos) atingido. Saindo da sala...`);

              // Mostrar notificação para o usuário se o navegador suportar
              if ('Notification' in window && Notification.permission === 'granted') {
                new Notification(t('sessionEndedTitle'), {
                  body: t('sessionTimeEnded').replace('{minutes}', MAX_SESSION_TIME.toString()),
                  icon: '/favicon.ico'
                });
              }

              leaveRoom();
            }
          }
        }, 60000); // Verificar a cada minuto
      } catch (error) {
        console.error('[AudioControlsOptimized] Erro ao entrar na sala:', error);
        // Garantir que o estado seja consistente mesmo em caso de erro
        setIsInRoom(false);
      }
    }
  }, [callObject, isInRoom, isLeaving, updateParticipantCount]);

  // Sair da sala
  const leaveRoom = useCallback(async () => {
    if (callObject && isInRoom && !isLeaving) {
      console.log('[AudioControlsOptimized] Saindo da sala...');
      setIsLeaving(true); // Indicar que está tentando sair

      try {
        // Forçar a atualização da UI imediatamente
        setIsInRoom(false);

        // Tentar sair da sala
        await callObject.leave();
        console.log('[AudioControlsOptimized] Saiu da sala com sucesso');

        // Atualizar timestamp da última atividade
        lastActivityRef.current = Date.now();

        // Limpar o temporizador de sessão máxima
        if (sessionTimerRef.current !== null) {
          window.clearInterval(sessionTimerRef.current);
          sessionTimerRef.current = null;
        }

        // Resetar o tempo de início da sessão
        sessionStartTimeRef.current = null;

        // Atualizar a contagem de participantes imediatamente após sair da sala
        // Primeiro, zerar a contagem localmente para feedback imediato
        setParticipantCount(0);

        // Depois, atualizar via API para garantir a precisão
        // Fazer várias tentativas para garantir que a contagem seja atualizada
        const updateCount = () => {
          updateParticipantCountViaAPI();
        };

        // Atualizar imediatamente
        updateCount();

        // E depois a cada 500ms por alguns segundos
        setTimeout(updateCount, 500);
        setTimeout(updateCount, 1000);
        setTimeout(updateCount, 1500);
        setTimeout(updateCount, 2000);
      } catch (error) {
        console.error('[AudioControlsOptimized] Erro ao sair da sala:', error);
        // Mesmo com erro, consideramos que saiu da sala para manter a UI consistente
        setIsInRoom(false);
      } finally {
        setIsLeaving(false); // Finalizar o processo de saída
      }
    }
  }, [callObject, isInRoom, isLeaving]);

  // Alternar entre entrar e sair da sala
  const toggleRoom = useCallback(() => {
    if (isInRoom) {
      leaveRoom();
    } else {
      joinRoom();
    }
  }, [isInRoom, joinRoom, leaveRoom]);

  // Lidar com falha na reprodução de áudio
  const handlePlayFailed = useCallback((e: any) => {
    console.error(`[AudioControlsOptimized] Falha ao reproduzir ${e.type} para ${e.sessionId}`);

    // Tentar reproduzir novamente
    if (e.target && typeof e.target.play === 'function') {
      setTimeout(() => {
        try {
          const playPromise = e.target.play();
          if (playPromise) {
            playPromise.catch((error: any) => {
              console.error(`[AudioControlsOptimized] Erro ao tentar reproduzir novamente: ${error}`);
            });
          }
        } catch (error) {
          console.error(`[AudioControlsOptimized] Erro ao tentar reproduzir novamente: ${error}`);
        }
      }, 1000);
    }
  }, []);

  // Renderizar o ícone do botão de sala com texto para torná-lo mais explícito
  const renderRoomButtonContent = () => {
    if (isInRoom) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px'
        }}>
          <span style={{ fontSize: '16px' }}>❌</span>
          <span style={{ fontSize: '12px', marginTop: '2px' }}>{t('leaveVoiceRoom')}</span>
          <span style={{
            fontSize: '10px',
            marginTop: '2px',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            borderRadius: '8px',
            padding: '1px 4px',
            color: 'white'
          }}>
            {`${participantCount} 👥`}
          </span>
        </div>
      );
    } else {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <span style={{ fontSize: '16px' }}>🎧</span>
          <span style={{ fontSize: '12px', marginTop: '2px' }}>{t('enterVoiceRoom')}</span>
          <span style={{
            fontSize: '10px',
            marginTop: '2px',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            borderRadius: '8px',
            padding: '1px 4px',
            color: 'white'
          }}>
            {`${participantCount} 👥`}
          </span>
        </div>
      );
    }
  };

  const { t } = useLanguage();

  return (
    <>
      {/* Botão de entrar/sair da sala */}
      <button
        onClick={toggleRoom}
        disabled={!isInitialized || isLeaving}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: isChatOpen ? '540px' : '200px', // Move para a esquerda quando o chat está aberto
          zIndex: 999,
          padding: '8px',
          borderRadius: '50%',
          backgroundColor: isInRoom ? 'rgba(244, 67, 54, 0.9)' : 'rgba(76, 175, 80, 0.9)',
          border: isInRoom ? '1px solid rgba(244, 67, 54, 0.5)' : '1px solid rgba(76, 175, 80, 0.5)',
          color: 'rgba(255, 255, 255, 0.9)',
          cursor: isInitialized && !isLeaving ? 'pointer' : 'not-allowed',
          width: '60px',
          height: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isInRoom ? '0 0 15px rgba(244, 67, 54, 0.5)' : '0 0 15px rgba(76, 175, 80, 0.5)',
          transition: 'all 0.3s ease, right 0.3s ease-in-out',
          opacity: isInitialized && !isLeaving ? 1 : 0.5
        }}
        title={isInRoom ? t('leaveVoiceRoom') : t('enterVoiceRoom')}
      >
        {isLeaving ? (
          <div style={{ fontSize: '14px' }}>⏳</div>
        ) : (
          renderRoomButtonContent()
        )}
      </button>

      {/* Botão de microfone */}
      <button
        onClick={toggleMicrophone}
        disabled={!isInRoom}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: isChatOpen ? '420px' : '80px', // Move para a esquerda quando o chat está aberto
          zIndex: 999,
          padding: '12px',
          borderRadius: '50%',
          backgroundColor: isMicrophoneEnabled ? 'rgba(76, 175, 80, 0.9)' : 'rgba(0, 0, 0, 0.9)',
          border: isMicrophoneEnabled ? '1px solid rgba(76, 175, 80, 0.5)' : '1px solid rgba(138, 43, 226, 0.3)',
          color: isMicrophoneEnabled ? 'rgba(255, 255, 255, 0.9)' : 'rgba(244, 67, 54, 0.9)',
          cursor: isInRoom ? 'pointer' : 'not-allowed',
          width: '50px',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isMicrophoneEnabled ? '0 0 15px rgba(76, 175, 80, 0.5)' : '0 2px 10px rgba(0,0,0,0.2)',
          transition: 'all 0.3s ease, right 0.3s ease-in-out',
          opacity: isInRoom ? 1 : 0.5
        }}
        title={isMicrophoneEnabled ? t('disableMicrophone') : t('enableMicrophone')}
      >
        {isMicrophoneEnabled ? '🎙️' : '🔇'}
      </button>

      {/* Botão de áudio */}
      <button
        onClick={toggleAudio}
        disabled={!isInRoom}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: isChatOpen ? '480px' : '140px', // Move para a esquerda quando o chat está aberto
          zIndex: 999,
          padding: '12px',
          borderRadius: '50%',
          backgroundColor: isAudioEnabled ? 'rgba(76, 175, 80, 0.9)' : 'rgba(0, 0, 0, 0.9)',
          border: isAudioEnabled ? '1px solid rgba(76, 175, 80, 0.5)' : '1px solid rgba(138, 43, 226, 0.3)',
          color: isAudioEnabled ? 'rgba(255, 255, 255, 0.9)' : 'rgba(244, 67, 54, 0.9)',
          cursor: isInRoom ? 'pointer' : 'not-allowed',
          width: '50px',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isAudioEnabled ? '0 0 15px rgba(76, 175, 80, 0.5)' : '0 2px 10px rgba(0,0,0,0.2)',
          transition: 'all 0.3s ease, right 0.3s ease-in-out',
          opacity: isInRoom ? 1 : 0.5
        }}
        title={isAudioEnabled ? t('disableAudio') : t('enableAudio')}
      >
        {isAudioEnabled ? '🔊' : '🔈'}
      </button>

      {/* Indicador de participantes (discreto) - visível apenas quando há participantes */}
      {participantCount > 0 && (
        <div
          style={{
            position: 'fixed',
            bottom: '85px',
            right: isChatOpen ? '540px' : '200px', // Move para a esquerda quando o chat está aberto
            zIndex: 999,
            padding: '4px 8px',
            borderRadius: '12px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            fontSize: '12px',
            fontWeight: 'bold',
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
            transition: 'right 0.3s ease-in-out',
          }}
          title={t('voiceRoomParticipants')}
        >
          {participantCount} 👥
        </div>
      )}

      {/* DailyProvider e DailyAudio (invisíveis) */}
      {callObject && isInRoom && (
        <DailyProvider callObject={callObject}>
          <DailyAudio
            onPlayFailed={handlePlayFailed}
            maxSpeakers={10}
          />
        </DailyProvider>
      )}
    </>
  );
};

export default AudioControlsOptimized;
